<script setup>
import { ref, reactive, onMounted, defineEmits } from 'vue'
import { helpers, requiredIf } from '@vuelidate/validators'
import { useToast } from 'primevue/usetoast'
import errorHandler from '@/helpers/errorhandler'
import FxReportContainer from '@/components/FxReportContainer'
import LaporanPersediaanBarangService from '@/services/LaporanPersediaanBarangService'
import ObatService from '@/services/ObatService'
import useVuelidate from '@vuelidate/core'
import { saveAs } from 'file-saver'
import dayjs from 'dayjs'

const toast = useToast()
const laporanService = new LaporanPersediaanBarangService()
const obatService = new ObatService()
let dialogViewer = ref(false)
let pdfSource = ref('')
let pdfTitle = ref('PDF Viewer')
let pdfFilename = ref('')

let isLoadingPrint = ref(false)
let isLoadingExport = ref(false)
let isLoading = ref(false)
let acObat = ref([])
let acBatch = ref([])
let form = reactive({
  date_from: null,
  date_to: null,
  obat: null,
})
let submitted = ref(false)

const rules = {
  date_from: {
    required: helpers.withMessage(
      'Tanggal harus diisi.',
      requiredIf(() => form.date_to !== '' && form.date_to !== null)
    ),
  },
  date_to: {
    required: helpers.withMessage(
      'Tanggal harus diisi.',
      requiredIf(() => form.date_from !== '' && form.date_from !== null)
    ),
  },
}

const v$ = useVuelidate(rules, form)

// eslint-disable-next-line no-unused-vars
const emit = defineEmits(['toggle'])

onMounted(async () => {})

function searchObat(e) {
  obatService
    //.get(`?filter[nama]=${e.query}&filter[status]=aktif`)
    // .get(`?filter[nama]=${e.query}`)
    .getByName(`?filter[nama]=${e.query}`)
    .then((res) => {
      acObat.value = res.data.data
    })
    .catch((err) => {
      errorHandler(err, 'Data Obat', toast)
    })
}

function selectObat() {
  acBatch.value = form.obat.batch
}

function search(isFormValid) {
  submitted.value = true

  if (!isFormValid) {
    return
  }

  onPrint()
}

function onExport(isFormValid) {
  submitted.value = true

  if (!isFormValid) {
    return
  }

  isLoadingExport.value = true
  let obatId = 0
  let startDate = ''
  let endDate = ''
  if (form.date_from) {
    startDate = dayjs(form.date_from).format('YYYY-MM-DD')
  }
  if (form.date_to) {
    endDate = dayjs(form.date_to).format('YYYY-MM-DD')
  }
  if (form.obat) {
    obatId = form.obat.id
  }
  isLoading.value = true
  // submitted = true
  laporanService
    .exportLaporanGlobalExcel(startDate, endDate, obatId)
    .then((res) => {
      isLoadingExport.value = false
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'laporan-global-persediaan.xls') //khusus ini pakai xlsx
      document.body.appendChild(link)
      link.click()
    })

    .catch((err) => {
      errorHandler(err, 'Data Laporan Piutang Usaha Detail', toast)
    })
    .finally(() => (isLoadingExport.value = false))
}

function onPrint(isFormValid) {
  submitted.value = true

  if (!isFormValid) {
    return
  }

  isLoadingPrint.value = true
  let obatId = 0
  let startDate = ''
  let endDate = ''
  if (form.date_from) {
    startDate = dayjs(form.date_from).format('YYYY-MM-DD')
  }
  if (form.date_to) {
    endDate = dayjs(form.date_to).format('YYYY-MM-DD')
  }
  if (form.obat) {
    obatId = form.obat.id
  }
  isLoading.value = true
  // submitted = true
  laporanService
    .exportLaporanGlobal(startDate, endDate, obatId)
    .then((res) => {
      const blob = new Blob([res.data], { type: 'application/pdf' })
      const pdfUrl = URL.createObjectURL(blob)
      pdfSource.value = pdfUrl + '#toolbar=0'
      pdfFilename.value = 'laporan-global-persediaan.pdf'
      pdfTitle.value = 'PDF Viewer (' + pdfFilename.value + ')'
      dialogViewer.value = true
    })

    .catch((err) => {
      errorHandler(err, 'Data Laporan Stok Global', toast)
    })
    .finally(() => ((isLoadingPrint.value = false), (isLoading.value = false)))
}

function onPrintPdf() {
  const iframe = document.createElement('iframe')
  iframe.src = pdfSource.value
  iframe.style.display = 'none'
  document.body.appendChild(iframe)

  // Use onload to make pdf preview work on firefox
  iframe.onload = () => {
    iframe.contentWindow.focus()
    iframe.contentWindow.print()
  }
}

function onDownloadPdf() {
  saveAs(pdfSource.value, pdfFilename.value)
}

function onToggle() {
  emit('toggle')
}

function resetForm() {
  //  submitted = false
  form.date_from = null
  form.date_to = null
  form.obat = null
}
</script>

<template>
  <fx-report-container
    title="Laporan Stok Global"
    :loading="isLoadingPrint"
    @toggle="onToggle"
  >
    <template #content>
      <form @submit.prevent="search(!v$.$invalid)">
        <div class="formgrid grid surface-ground m-1 py-3">
          <div class="field col-12">
            <div class="font-bold">Filter</div>
          </div>
          <div class="field col-12 md:col-4">
            <AutoComplete
              v-model="form.obat"
              :min-length="2"
              :suggestions="acObat"
              field="nama"
              class="w-full"
              input-class="w-full"
              placeholder="Obat"
              autofocus
              @complete="searchObat($event)"
              @item-select="selectObat"
            >
            </AutoComplete>
          </div>
          <div class="field col-12 md:col-2">
            <Calendar
              v-model="v$.date_from.$model"
              dateFormat="dd/mm/yy"
              :class="{
                'p-invalid': v$.date_from.$invalid && submitted,
              }"
              placeholder="Tanggal (dari)"
              class="w-full"
            />
            <small
              v-if="
                (v$.date_from.$invalid && submitted) ||
                v$.date_from.$pending.$response
              "
              class="p-error"
              >{{ v$.date_from.required.$message }}</small
            >
          </div>
          <div class="field col-12 md:col-2">
            <Calendar
              v-model="v$.date_to.$model"
              dateFormat="dd/mm/yy"
              :class="{
                'p-invalid': v$.date_to.$invalid && submitted,
              }"
              placeholder="Tanggal (s/d)"
              class="w-full"
            />
            <small
              v-if="
                (v$.date_to.$invalid && submitted) ||
                v$.date_to.$pending.$response
              "
              class="p-error"
              >{{ v$.date_to.required.$message }}</small
            >
          </div>
          <div class="field col-12 md:col-3">
            <Button
              type="button"
              icon="pi pi-file-pdf"
              label="Cetak"
              class="mb-0 md:mb-2 xl:mb-0"
              @click="onPrint(!v$.$invalid)"
            />
            <Button
              :loading="isLoadingExport"
              type="button"
              icon="pi pi-file-excel"
              label="Export"
              class="p-button-success ml-2 md:ml-0 xl:ml-2 mb-0 md:mb-2 xl:mb-0"
              @click="onExport(!v$.$invalid)"
            />
            <Button
              type="reset"
              icon="pi pi-refresh"
              label="Reset"
              class="p-button-outlined ml-2 md:ml-0 xl:ml-2 btn-ml-0 btn-mt-2"
              @click="resetForm"
            />
          </div>
        </div>
      </form>
    </template>
  </fx-report-container>
  <Dialog
    :header="pdfTitle"
    v-model:visible="dialogViewer"
    :breakpoints="{ '960px': '75vw', '640px': '90vw' }"
    :style="{ width: '90vw' }"
    :modal="true"
  >
    <div>
      <embed
        :src="pdfSource"
        type="application/pdf"
        width="100%"
        height="600px"
      />
    </div>
    <template #footer>
      <Button
        type="button"
        label="Download"
        icon="pi pi-download"
        class="p-button-text mr-2"
        @click="onDownloadPdf"
      />
      <Button
        type="button"
        label="Print"
        icon="pi pi-print"
        @click="onPrintPdf"
      />
    </template>
  </Dialog>
</template>
